import imageCompression from 'browser-image-compression'
import heic2any from 'heic2any'

interface ImageProcessingOptions {
  maxHeight?: number
  maxWidth?: number
  quality?: number
}

const defaultOptions: ImageProcessingOptions = {
  maxHeight: 500,
  quality: 0.5,
}

/**
 * Check if file is HEIC/HEIF format
 */
const isHeicFile = (file: File): boolean => {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  )
}

/**
 * Try to convert image using native browser canvas support
 * Works for browsers that natively support HEIC (Safari, some Chrome versions)
 */
const convertWithNativeSupport = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0)

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url)
            if (blob) {
              const originalName = file.name.replace(/\.[^/.]+$/, '')
              resolve(
                new File([blob], `${originalName}.jpg`, { type: 'image/jpeg' })
              )
            } else {
              reject(new Error('Canvas toBlob failed'))
            }
          },
          'image/jpeg',
          0.9
        )
      } catch (error) {
        URL.revokeObjectURL(url)
        reject(error)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Browser cannot load this image format'))
    }

    img.src = url
  })
}

/**
 * Convert HEIC/HEIF file to JPEG
 * Tries native browser support first, falls back to heic2any
 */
const convertHeicToJpeg = async (file: File): Promise<File> => {
  // Try native browser support first (works in Safari and some other browsers)
  try {
    console.log('Trying native browser HEIC support...')
    const result = await convertWithNativeSupport(file)
    console.log('Native conversion successful')
    return result
  } catch (nativeError) {
    console.log('Native support failed, trying heic2any...', nativeError)
  }

  // Fall back to heic2any library
  try {
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9,
    })

    const resultBlob = Array.isArray(blob) ? blob[0] : blob
    const originalName = file.name.replace(/\.[^/.]+$/, '')
    console.log('heic2any conversion successful')
    return new File([resultBlob], `${originalName}.jpg`, { type: 'image/jpeg' })
  } catch (error) {
    console.error('HEIC conversion failed:', error)
    throw new Error(
      'Unable to convert HEIC image. Please convert it to JPG or PNG before uploading.'
    )
  }
}

/**
 * Process an image file: resize, convert format, and compress
 * - Maintains aspect ratio
 * - Converts to JPEG format
 * - Applies compression quality (default: 50%)
 * - Handles HEIC/HEIF and other formats
 * - Preserves EXIF orientation
 */
export const processImageAsFile = async (
  file: File,
  options: ImageProcessingOptions = {}
): Promise<File> => {
  const { maxHeight, maxWidth, quality } = {
    ...defaultOptions,
    ...options,
  }

  // Convert HEIC/HEIF to JPEG first
  let processableFile = file
  if (isHeicFile(file)) {
    console.log('HEIC file detected, converting...')
    processableFile = await convertHeicToJpeg(file)
    console.log('HEIC conversion complete')
  }

  const compressionOptions = {
    maxWidthOrHeight: maxHeight || maxWidth || 500,
    initialQuality: quality,
    useWebWorker: true,
    fileType: 'image/jpeg' as const,
    preserveExif: false,
  }

  console.log('Starting compression...')
  const compressedFile = await imageCompression(
    processableFile,
    compressionOptions
  )
  console.log('Compression complete')

  // Ensure the file has .jpg extension
  const originalName = file.name.replace(/\.[^/.]+$/, '')
  const newFileName = `${originalName}.jpg`

  return new File([compressedFile], newFileName, { type: 'image/jpeg' })
}
